import { Environment } from 'src/Environment';
import { Api } from 'src/common';
import { authenticate } from 'src/services/auth-service';
import { getUserId } from 'src/services/user.service';

export type PaymentEnabledResponse = {
  merchantNumber: string;
  paymentRequestEnabled: boolean;
};

export type PaymentResult = {
  transactionId: string;
  paymentLink: string;
};

export type PaymentRequestResponse = {
  transactionId: string;
  paymentLink: string;
};

export enum PaymentType {
  FixedAmount = 'FixedAmount',
  VariableAmount = 'VariableAmount',
  None = '',
}

export enum PaymentScope {
  SingleCustomer = 'SingleCustomer',
  MultipleCustomers = 'MultipleCustomers',
}

export enum NotificationMethod {
  Email = 'Email',
  SMS = 'SMS',
  None = '',
}

export type PaymentRequest = {
  sourceTransactionId: string;
  paymentScope: PaymentScope;
  paymentType: PaymentType;
  reference?: string; // optional, only required for FixedAmount paymentType
  amountInCents?: number; // optional, not required for VariableAmount paymentType
  notificationType: NotificationMethod;
  notificationAddress: string;
};

export async function isPaymentRequestEnabled(merchantNumber: string): Promise<boolean> {
  const accessToken = await authenticate();
  const userId = getUserId();

  if (!userId || !accessToken) {
    throw new Error('Unable to get payment status');
  }

  const paymentEnabledUrl = `/merchants/${merchantNumber}/paymentRequestEnabled?userId=${userId}`;

  const { response, status } = await Api.getV2<PaymentEnabledResponse>(
    Environment.current.privateBaseURL + paymentEnabledUrl,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (status !== 200) {
    throw new Error('Unable to get payment status');
  }

  return response?.paymentRequestEnabled;
}

export async function paymentRequests(
  payload: PaymentRequest,
  merchantNumber: string,
): Promise<{
  transactionId: string;
  paymentLink: string;
}> {
  const accessToken = await authenticate();
  const userId = getUserId();

  if (!userId || !accessToken) {
    throw new Error('Unable to get payment status');
  }

  const paymentRequestUrl = `/merchants/${merchantNumber}/paymentRequests`;

  const { response, status } = await Api.postV2<PaymentRequestResponse>(
    Environment.current.privateBaseURL + paymentRequestUrl,
    {
      body: JSON.stringify({
        userId,
        ...payload,
      }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (status !== 200) {
    throw new Error('Unable to get payment status');
  }

  return response;
}
